import { useState } from "react";
import { Minus, Plus, Trash2, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { useCart } from "../../lib/cart";
import { DELIVERY_TEXT, formatErr, gbp, http, waLink } from "../../lib/api";

export const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, total } = useCart();
  const hasDash = items.some((i) => i.type === "dashboard");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await http.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatErr(e));
      setLoading(false);
    }
  };

  const waText = `Hi CBLS AIRBAGS, I'd like to order:\n${items.map((i) => `- ${i.qty} x ${i.title}`).join("\n")}`;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="flex w-full flex-col border-white/10 bg-[#0D1218] sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="font-display text-3xl font-extrabold uppercase">Your basket</SheetTitle>
          <SheetDescription className="text-slate-400">Prices in GBP.</SheetDescription>
        </SheetHeader>
        <div className="-mx-6 mt-4 flex-1 overflow-y-auto px-6" data-lenis-prevent>
          {items.length === 0 && (
            <p data-testid="cart-empty" className="py-16 text-center text-slate-400">Your basket is empty.</p>
          )}
          {items.map((i) => (
            <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex gap-4 border-b border-white/10 py-4">
              <img src={i.image} alt="" className="clip-frame h-20 w-20 object-cover" />
              <div className="flex-1">
                <p className="text-sm font-semibold leading-snug">{i.title}</p>
                <p className="mt-1 font-mono text-sm text-slate-300">{gbp(i.price)}</p>
                <div className="mt-2 flex items-center gap-2">
                  <button data-testid={`cart-dec-${i.id}`} onClick={() => setQty(i.id, i.qty - 1)} className="rounded-full border border-white/15 p-1.5 hover:bg-white/10"><Minus className="h-3 w-3" /></button>
                  <span data-testid={`cart-qty-${i.id}`} className="w-6 text-center font-mono text-sm">{i.qty}</span>
                  <button data-testid={`cart-inc-${i.id}`} onClick={() => setQty(i.id, i.qty + 1)} className="rounded-full border border-white/15 p-1.5 hover:bg-white/10"><Plus className="h-3 w-3" /></button>
                  <button data-testid={`cart-remove-${i.id}`} onClick={() => remove(i.id)} className="ml-auto text-slate-400 hover:text-[#F43F5E]" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div className="space-y-3 border-t border-white/10 pt-4">
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-xs uppercase tracking-widest text-slate-400">Parts</span>
              <span data-testid="cart-total" className="font-display text-3xl font-bold">{gbp(total)}</span>
            </div>
            <div data-testid="cart-delivery" className="flex items-baseline justify-between gap-4 border-t border-white/10 pt-3">
              <span className="font-mono text-xs uppercase tracking-widest text-slate-400">Delivery</span>
              <span className="font-display text-xl font-bold">{hasDash ? gbp(70) : `from ${gbp(4.99)}`}</span>
            </div>
            <p className="text-xs text-slate-400">{hasDash ? DELIVERY_TEXT.dashboard : DELIVERY_TEXT.standard} Choose at checkout.</p>
            <p className="text-xs text-slate-400">Pay by card, Apple Pay, PayPal or Klarna.</p>
            <button
              data-testid="cart-checkout-button"
              onClick={checkout}
              disabled={loading}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#E11D48] font-semibold transition-colors hover:bg-[#F43F5E] disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Checkout securely
            </button>
            <a
              data-testid="cart-whatsapp-order"
              href={waLink(waText)}
              target="_blank"
              rel="noreferrer"
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full border border-white/15 text-sm font-semibold hover:border-emerald-400 hover:text-emerald-400"
            >
              <MessageCircle className="h-4 w-4" /> Or order on WhatsApp
            </a>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
